import { RetrievalMethod } from '@/feature/profiles/constants';
import { Facesheet } from '@/feature/profiles/types';

export type FacesheetTemplateType = 'FULL' | 'HIE';

export interface FacesheetTemplateCapabilities {
  hasFullTemplate: boolean;
  hasHieTemplate: boolean;
}

export interface FacesheetsValidationResult {
  isValid: boolean;
  errorMessage?: string;
  missingMethods?: RetrievalMethod[];
}

interface ValidateFacesheetsSelectionParams {
  selectedMethods: RetrievalMethod[];
  capabilities: FacesheetTemplateCapabilities;
  allowStandaloneFacesheets?: boolean;
  isAdvanced?: boolean;
}

interface GetAllowedFacesheetsParams {
  selectedMethods: RetrievalMethod[];
  facesheets: Facesheet[];
  allowStandaloneFacesheets?: boolean;
  isAdvanced?: boolean;
}

interface IsFacesheetAllowedParams {
  selectedMethods: RetrievalMethod[];
  facesheet: Facesheet;
  allowStandaloneFacesheets?: boolean;
  isAdvanced?: boolean;
}

export const FACESHEETS_VALIDATION_MESSAGES = {
  FACESHEETS_REQUIRED_FOR_HIE: 'Facesheets are required to retrieve HIE data.',
  HIE_REQUIRED: 'Select Digital Health Data to generate Facesheets.',
  FULL_REQUIRED: 'Select Provider/Facility Retrieval to generate Facesheets.',
  FULL_OR_HIE_REQUIRED:
    'Select Provider/Facility Retrieval or Digital Health Data to generate Facesheets.',
  FS2_MEDICATIONS_SOURCE_REQUIRED:
    'Select at least one of Provider/Facility Retrieval, Digital Health Data, or Medical Record Upload to generate Facesheets with Medications Data.',
  FS2_DATA_SOURCE_REQUIRED:
    'Select Provider/Facility Retrieval, Digital Health Data or Medical Record Upload to generate Facesheets.',
  REQUEST_TYPE_REQUIRED:
    'Select at least one request type to generate Facesheets.',
} as const;

// FS2.0 (advanced): Facesheets can be generated from any data source.
const DATA_SOURCE_METHODS: RetrievalMethod[] = [
  RetrievalMethod.PROVIDER_FACILITY,
  RetrievalMethod.HIE,
  RetrievalMethod.MEDICATIONS,
  RetrievalMethod.RECORD_UPLOAD,
];

const FS2_MEDICATIONS_SUPPORTING_SOURCE_METHODS: RetrievalMethod[] = [
  RetrievalMethod.PROVIDER_FACILITY,
  RetrievalMethod.HIE,
  RetrievalMethod.RECORD_UPLOAD,
];

const hasTemplateType = (
  facesheet: Facesheet,
  templateType: FacesheetTemplateType,
) =>
  facesheet.templateTypes.some(
    (value) => value.toUpperCase() === templateType.toUpperCase(),
  );

export const deriveFacesheetTemplateCapabilities = (
  facesheets: Facesheet[],
): FacesheetTemplateCapabilities => ({
  hasFullTemplate: facesheets.some((facesheet) =>
    hasTemplateType(facesheet, 'FULL'),
  ),
  hasHieTemplate: facesheets.some((facesheet) =>
    hasTemplateType(facesheet, 'HIE'),
  ),
});

export const validateFacesheetsSelection = ({
  selectedMethods,
  capabilities,
  allowStandaloneFacesheets = false,
  isAdvanced = false,
}: ValidateFacesheetsSelectionParams): FacesheetsValidationResult => {
  const isFacesheetsSelected = selectedMethods.includes(
    RetrievalMethod.FACESHEETS,
  );

  // FS2.0: template-type to data-source coupling is removed, but HIE requests
  // still require a Facesheets request.
  if (isAdvanced) {
    const isHieSelected = selectedMethods.includes(RetrievalMethod.HIE);
    if (isHieSelected && !isFacesheetsSelected) {
      return {
        isValid: false,
        errorMessage:
          FACESHEETS_VALIDATION_MESSAGES.FACESHEETS_REQUIRED_FOR_HIE,
        missingMethods: [RetrievalMethod.FACESHEETS],
      };
    }

    if (!isFacesheetsSelected) return { isValid: true };

    const isMedicationsSelected = selectedMethods.includes(
      RetrievalMethod.MEDICATIONS,
    );
    const hasSupportingMedicationsSource =
      FS2_MEDICATIONS_SUPPORTING_SOURCE_METHODS.some((method) =>
        selectedMethods.includes(method),
      );

    if (isMedicationsSelected && !hasSupportingMedicationsSource) {
      return {
        isValid: false,
        errorMessage:
          FACESHEETS_VALIDATION_MESSAGES.FS2_MEDICATIONS_SOURCE_REQUIRED,
        missingMethods: FS2_MEDICATIONS_SUPPORTING_SOURCE_METHODS,
      };
    }

    const hasDataSourceSelection = DATA_SOURCE_METHODS.some((method) =>
      selectedMethods.includes(method),
    );
    if (hasDataSourceSelection) {
      return { isValid: true };
    }

    // Core does not yet support standalone FS2 generation from a patient's
    // eligible historical data, even when canCreate is true.
    if (allowStandaloneFacesheets) {
      return {
        isValid: false,
        errorMessage: FACESHEETS_VALIDATION_MESSAGES.FS2_DATA_SOURCE_REQUIRED,
        missingMethods: FS2_MEDICATIONS_SUPPORTING_SOURCE_METHODS,
      };
    }

    return {
      isValid: false,
      errorMessage: FACESHEETS_VALIDATION_MESSAGES.REQUEST_TYPE_REQUIRED,
      missingMethods: DATA_SOURCE_METHODS.filter(
        (method) => !selectedMethods.includes(method),
      ),
    };
  }

  const isHieSelected = selectedMethods.includes(RetrievalMethod.HIE);
  const isProviderFacilitySelected = selectedMethods.includes(
    RetrievalMethod.PROVIDER_FACILITY,
  );
  const hasRetrievalSelection = isHieSelected || isProviderFacilitySelected;

  if (isHieSelected && !isFacesheetsSelected) {
    // HIE only forces Facesheets when this profile has HIE-capable templates.
    if (capabilities.hasHieTemplate) {
      return {
        isValid: false,
        errorMessage:
          FACESHEETS_VALIDATION_MESSAGES.FACESHEETS_REQUIRED_FOR_HIE,
        missingMethods: [RetrievalMethod.FACESHEETS],
      };
    }
  }

  if (!isFacesheetsSelected) return { isValid: true };

  if (capabilities.hasHieTemplate && !capabilities.hasFullTemplate) {
    if (isHieSelected || allowStandaloneFacesheets) return { isValid: true };
    return {
      isValid: false,
      errorMessage: FACESHEETS_VALIDATION_MESSAGES.HIE_REQUIRED,
      missingMethods: [RetrievalMethod.HIE],
    };
  }

  if (capabilities.hasFullTemplate && !capabilities.hasHieTemplate) {
    if (isProviderFacilitySelected || allowStandaloneFacesheets)
      return { isValid: true };
    return {
      isValid: false,
      errorMessage: FACESHEETS_VALIDATION_MESSAGES.FULL_REQUIRED,
      missingMethods: [RetrievalMethod.PROVIDER_FACILITY],
    };
  }

  if (capabilities.hasFullTemplate && capabilities.hasHieTemplate) {
    if (hasRetrievalSelection) return { isValid: true };
    if (allowStandaloneFacesheets) {
      return { isValid: true };
    }
    return {
      isValid: false,
      errorMessage: FACESHEETS_VALIDATION_MESSAGES.FULL_OR_HIE_REQUIRED,
      missingMethods: [
        RetrievalMethod.PROVIDER_FACILITY,
        RetrievalMethod.HIE,
      ].filter((method) => !selectedMethods.includes(method)),
    };
  }

  return { isValid: true };
};

export const getAllowedFacesheets = ({
  selectedMethods,
  facesheets,
  allowStandaloneFacesheets = false,
  isAdvanced = false,
}: GetAllowedFacesheetsParams): Facesheet[] => {
  const isFacesheetsSelected = selectedMethods.includes(
    RetrievalMethod.FACESHEETS,
  );

  if (!isFacesheetsSelected) return [];

  // FS2.0: all profile templates are available regardless of selected methods.
  if (isAdvanced) return facesheets;

  const isHieSelected = selectedMethods.includes(RetrievalMethod.HIE);
  const isProviderFacilitySelected = selectedMethods.includes(
    RetrievalMethod.PROVIDER_FACILITY,
  );

  if (isHieSelected && isProviderFacilitySelected) {
    return facesheets.filter(
      (facesheet) =>
        hasTemplateType(facesheet, 'HIE') || hasTemplateType(facesheet, 'FULL'),
    );
  }

  if (isHieSelected) {
    return facesheets.filter((facesheet) => hasTemplateType(facesheet, 'HIE'));
  }

  if (isProviderFacilitySelected) {
    return facesheets.filter((facesheet) => hasTemplateType(facesheet, 'FULL'));
  }

  if (allowStandaloneFacesheets) {
    return facesheets.filter((facesheet) => hasTemplateType(facesheet, 'FULL'));
  }

  return [];
};

export const isFacesheetAllowed = ({
  selectedMethods,
  facesheet,
  allowStandaloneFacesheets = false,
  isAdvanced = false,
}: IsFacesheetAllowedParams): boolean =>
  getAllowedFacesheets({
    selectedMethods,
    facesheets: [facesheet],
    allowStandaloneFacesheets,
    isAdvanced,
  }).length > 0;
